import * as React from 'react';
import { cn } from '../lib/cn';
import { chartColors } from '../design-system/tokens/chartColors';

/**
 * ChartLine — a measure over time.
 *
 * Two variants, and the difference is not cosmetic:
 *
 *   'default' — a chart. Grid, axis labels, area fill, selective value labels.
 *   'spark'   — a SHAPE, next to a number that is the actual measurement. No
 *               axes, no grid, no labels; 44px tall. A sparkline answers "which
 *               way is this going", never "how much" — the hero figure beside
 *               it answers that.
 *
 * ── The one thing this component exists to get right ─────────────────────────
 * A point flagged `partial` is an INCOMPLETE PERIOD. The month in progress is
 * drawn with a dashed leading segment and a hollow endpoint, because rendered
 * solid alongside completed months a half-finished month reads as a collapse.
 * On 19 Aug 2026 collections stood at R1 769 against July's R2 520 — a 30% drop
 * that is really 13 days of a month that has not happened yet.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Labels are SELECTIVE by default: the peak and the last point, nothing else.
 * A number on every point is noise, and the grid already answers "roughly how
 * much" for the ones in between.
 */

export interface ChartLinePoint {
  label: string;
  value: number;
  /** True for a period still in progress. Drawn dashed with a hollow endpoint. */
  partial?: boolean;
}

export interface ChartLineProps {
  data: ChartLinePoint[];
  variant?: 'default' | 'spark';
  /** 'peak-and-last' (default) labels only those two points. */
  labelStrategy?: 'peak-and-last' | 'none';
  /**
   * Formats a POINT label — the peak and the last value. Must be EXACT.
   *
   * This is separate from `axisFormat` because sharing one formatter shipped a
   * real defect on 2026-08-19: the compact axis format was reused for point
   * labels, so a peak of R2 520 was labelled "3k" and R1 769 was labelled "2k"
   * directly under a hero figure reading "R 1 769". A rounded axis tick is
   * fine — the gridline it sits on IS that value. A rounded point label is a
   * number the chart is asserting, and it must be the real one.
   */
  format?: (value: number) => string;
  /**
   * Formats a GRIDLINE label. Defaults to `format`. Compact is appropriate
   * here — these are the axis steps themselves, not measurements of the data.
   */
  axisFormat?: (value: number) => string;
  /** Describes the shape in words for a screen reader. Required. */
  ariaLabel: string;
  className?: string;
}

/** Round a maximum up to something a human would put on an axis. */
function niceMax(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const steps = [1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10];
  for (const s of steps) {
    if (value <= s * magnitude) return s * magnitude;
  }
  return 10 * magnitude;
}

export function ChartLine({
  data,
  variant = 'default',
  labelStrategy = 'peak-and-last',
  format = (v) => v.toLocaleString(),
  axisFormat,
  ariaLabel,
  className,
}: ChartLineProps) {
  const axisFmt = axisFormat ?? format;
  // Unique per instance — two charts on one page would otherwise share a
  // gradient id and the second would silently adopt the first's fill.
  const gradientId = React.useId();
  const spark = variant === 'spark';

  if (data.length === 0) return null;

  const W = spark ? 150 : 640;
  const H = spark ? 46 : 220;
  const padL = spark ? 4 : 56;
  const padR = spark ? 6 : 16;
  const padT = spark ? 8 : 16;
  const padB = spark ? 8 : 28;

  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  const peak = data.reduce((m, d) => Math.max(m, d.value), 0);
  const top = spark ? Math.max(peak, 1) : niceMax(peak);

  const x = (i: number) => (data.length === 1 ? padL + plotW / 2 : padL + (i * plotW) / (data.length - 1));
  const y = (v: number) => padT + plotH * (1 - Math.min(Math.max(v / top, 0), 1));

  const pts = data.map((d, i) => ({ ...d, x: x(i), y: y(d.value) }));

  // The first partial point starts the dashed run; everything from there on is
  // provisional, so the split is by index rather than per-segment.
  const firstPartial = pts.findIndex((p) => p.partial);
  const solidEnd = firstPartial === -1 ? pts.length : firstPartial;
  const solid = pts.slice(0, solidEnd);
  // Overlap by one so the dashed run visually continues from the solid one
  // rather than starting after a gap.
  const dashed = firstPartial === -1 ? [] : pts.slice(Math.max(solidEnd - 1, 0));

  const line = (ps: typeof pts) => ps.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

  const baseline = (padT + plotH).toFixed(1);
  const first = pts[0];
  const last = pts[pts.length - 1];
  const areaPath =
    first && last
      ? `M ${pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' L ')}` +
        ` L ${last.x.toFixed(1)},${baseline} L ${first.x.toFixed(1)},${baseline} Z`
      : '';

  const peakIndex = pts.reduce((best, p, i) => (p.value > (pts[best]?.value ?? -Infinity) ? i : best), 0);
  const lastIndex = pts.length - 1;
  const labelled =
    labelStrategy === 'none' ? new Set<number>() : new Set([peakIndex, lastIndex]);

  const gridLines = spark ? [] : [0, 1 / 3, 2 / 3, 1];

  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      viewBox={`0 0 ${W} ${H}`}
      className={cn('block', spark ? 'h-[46px] w-[150px] shrink-0' : 'h-auto w-full', className)}
      // NOT preserveAspectRatio="none". Stretching the viewBox to fill a wide
      // container would stretch the 2px stroke and every label with it. The
      // chart scales proportionally instead, so a 640x220 viewBox keeps its
      // shape at any width.
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={chartColors.categorical[0]} stopOpacity="0.2" />
          <stop offset="100%" stopColor={chartColors.categorical[0]} stopOpacity="0.02" />
        </linearGradient>
      </defs>

      {gridLines.map((t) => {
        const gy = padT + plotH * (1 - t);
        return (
          <g key={t}>
            <line x1={padL} y1={gy} x2={W - padR} y2={gy} stroke={chartColors.grid} strokeWidth="1" />
            <text
              x={padL - 10}
              y={gy + 3.5}
              textAnchor="end"
              fontSize="10"
              fontFamily="JetBrains Mono, monospace"
              fill={chartColors.axisLabel}
            >
              {axisFmt(Math.round(top * t))}
            </text>
          </g>
        );
      })}

      {!spark && <path d={areaPath} fill={`url(#${gradientId})`} />}

      {solid.length > 1 && (
        <polyline
          points={line(solid)}
          fill="none"
          stroke={chartColors.categorical[0]}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      {dashed.length > 1 && (
        <polyline
          points={line(dashed)}
          fill="none"
          stroke={chartColors.categorical[0]}
          strokeWidth="2"
          strokeDasharray="5 4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}

      {pts.map((p, i) => {
        const isLast = i === lastIndex;
        const hollow = p.partial === true;
        if (spark && !isLast) return null;
        return (
          <circle
            key={`${p.label}-${i}`}
            cx={p.x}
            cy={p.y}
            r={isLast ? 5 : 3.5}
            fill={hollow ? '#ffffff' : chartColors.categorical[0]}
            stroke={hollow || isLast ? chartColors.categorical[0] : undefined}
            strokeWidth={hollow || isLast ? 2 : undefined}
          />
        );
      })}

      {!spark &&
        pts.map((p, i) =>
          labelled.has(i) ? (
            <text
              key={`lbl-${p.label}-${i}`}
              x={p.x}
              y={p.y - 12}
              // Clamp at the edges. A centred label on the LAST point runs off
              // the viewBox once it is more than a few characters wide — "2k"
              // fits where "R 1 769" does not, which is why the exact-format
              // fix needed this one alongside it.
              textAnchor={p.x > W - 60 ? 'end' : p.x < padL + 24 ? 'start' : 'middle'}
              fontSize="11"
              fontWeight="600"
              fontFamily="JetBrains Mono, monospace"
              // Ink, never the series colour (DC-7).
              fill="currentColor"
              className="text-foreground"
            >
              {format(p.value)}
            </text>
          ) : null,
        )}

      {!spark &&
        pts.map((p, i) => (
          <text
            key={`ax-${p.label}-${i}`}
            x={p.x}
            y={H - 10}
            textAnchor="middle"
            fontSize="10"
            fontFamily="JetBrains Mono, monospace"
            fill={chartColors.axisLabel}
          >
            {p.label}
          </text>
        ))}
    </svg>
  );
}
